import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { updateProfile, changePassword, adminGetUsers, adminCreateUser, adminUpdateUser, adminDeleteUser } from "./api";

const S = {
  overlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:100, display:"flex", alignItems:"flex-end", justifyContent:"center", fontFamily:"Inter,sans-serif" },
  sheet:   { width:"100%", maxWidth:580, background:"#fff", borderRadius:"16px 16px 0 0", maxHeight:"88vh", overflowY:"auto", paddingBottom:32 },
  header:  { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"18px 20px 14px", borderBottom:"1px solid #f0f0f0", position:"sticky", top:0, background:"#fff", zIndex:1 },
  title:   { fontSize:16, fontWeight:700, color:"#111" },
  close:   { width:32, height:32, borderRadius:"50%", border:"none", background:"#f0f0f0", cursor:"pointer", fontSize:16, color:"#666" },
  section: { padding:"16px 20px 0" },
  label:   { display:"block", fontSize:10, color:"#bbb", fontWeight:700, letterSpacing:"0.06em", marginBottom:5 },
  input:   { width:"100%", padding:"9px 11px", borderRadius:8, border:"1px solid #e0e0e0", fontSize:13, fontFamily:"Inter,sans-serif", outline:"none", background:"#fafafa", marginBottom:12, boxSizing:"border-box" },
  btn:     { padding:"9px 18px", borderRadius:8, border:"none", cursor:"pointer", fontFamily:"Inter,sans-serif", fontSize:12, fontWeight:600 },
  btnDark: { background:"#1a1a1a", color:"#fff" },
  btnGray: { background:"#f0f0f0", color:"#666" },
  btnRed:  { background:"#fbe9e7", color:"#bf360c", border:"1px solid #ffab91" },
  err:     { background:"#fbe9e7", border:"1px solid #ff8a65", borderRadius:8, padding:"8px 11px", fontSize:11, color:"#bf360c", marginBottom:10 },
  ok:      { background:"#e8f5e9", border:"1px solid #a5d6a7", borderRadius:8, padding:"8px 11px", fontSize:11, color:"#2e7d32", marginBottom:10 },
  tabs:    { display:"flex", gap:0, borderBottom:"1px solid #f0f0f0", padding:"0 20px" },
  tab:     { padding:"10px 14px", fontSize:12, fontWeight:600, cursor:"pointer", border:"none", background:"transparent", fontFamily:"Inter,sans-serif" },
  userRow: { display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:"1px solid #f7f7f7" },
  badge:   { fontSize:9, padding:"2px 7px", borderRadius:10, fontWeight:700 },
};

function Field({ label, value, onChange, type="text", placeholder="" }) {
  return (
    <>
      <label style={S.label}>{label}</label>
      <input style={S.input} type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </>
  );
}

// ── My Profile Tab ────────────────────────────────────────────────────────────

function ProfileTab() {
  const { user, refreshUser } = useAuth();
  const [disp,    setDisp]    = useState(user?.display_name || "");
  const [email,   setEmail]   = useState(user?.email || "");
  const [curPw,   setCurPw]   = useState("");
  const [newPw,   setNewPw]   = useState("");
  const [msg,     setMsg]     = useState({ type:"", text:"" });
  const [busy,    setBusy]    = useState(false);

  async function saveProfile(e) {
    e.preventDefault(); setBusy(true); setMsg({});
    try {
      await updateProfile({ display_name: disp, email });
      await refreshUser();
      setMsg({ type:"ok", text:"Profil gespeichert." });
    } catch(ex) { setMsg({ type:"err", text: ex.message }); }
    finally { setBusy(false); }
  }

  async function savePw(e) {
    e.preventDefault(); setBusy(true); setMsg({});
    try {
      await changePassword({ current_password: curPw, new_password: newPw });
      setCurPw(""); setNewPw("");
      setMsg({ type:"ok", text:"Passwort geändert." });
    } catch(ex) { setMsg({ type:"err", text: ex.message }); }
    finally { setBusy(false); }
  }

  return (
    <div style={S.section}>
      {msg.text && <div style={msg.type==="ok" ? S.ok : S.err}>{msg.text}</div>}

      <div style={{ fontSize:11, color:"#bbb", marginBottom:14 }}>
        Konto: <strong style={{ color:"#333" }}>@{user?.username}</strong> ·
        Rolle: <strong style={{ color:"#333" }}>{user?.role === "admin" ? "Administrator" : "Benutzer"}</strong>
      </div>

      <form onSubmit={saveProfile}>
        <Field label="ANZEIGENAME"    value={disp}  onChange={setDisp}  placeholder="Dein Name" />
        <Field label="E-MAIL" type="email" value={email} onChange={setEmail} />
        <button style={{ ...S.btn, ...S.btnDark, opacity:busy?0.6:1 }} disabled={busy}>Profil speichern</button>
      </form>

      <div style={{ height:1, background:"#f0f0f0", margin:"18px 0" }} />

      <form onSubmit={savePw}>
        <Field label="AKTUELLES PASSWORT" type="password" value={curPw} onChange={setCurPw} />
        <Field label="NEUES PASSWORT"     type="password" value={newPw} onChange={setNewPw} />
        <button style={{ ...S.btn, ...S.btnGray, opacity:busy?0.6:1 }} disabled={busy}>Passwort ändern</button>
      </form>
    </div>
  );
}

// ── Admin Tab ─────────────────────────────────────────────────────────────────

function AdminTab() {
  const { user: me } = useAuth();
  const [users,   setUsers]   = useState([]);
  const [editing, setEditing] = useState(null); // user id or "new"
  const [form,    setForm]    = useState({});
  const [msg,     setMsg]     = useState({ type:"", text:"" });
  const [busy,    setBusy]    = useState(false);

  const load = useCallback(async () => {
    try { setUsers(await adminGetUsers()); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  function startEdit(u) {
    setEditing(u.id);
    setForm({ display_name: u.display_name || "", email: u.email, role: u.role, password:"" });
    setMsg({});
  }
  function startNew() {
    setEditing("new");
    setForm({ username:"", display_name:"", email:"", password:"", role:"user" });
    setMsg({});
  }

  async function save(e) {
    e.preventDefault(); setBusy(true); setMsg({});
    try {
      if (editing === "new") {
        await adminCreateUser(form);
        setMsg({ type:"ok", text:"Benutzer erstellt." });
      } else {
        const payload = { display_name: form.display_name, email: form.email, role: form.role };
        if (form.password) payload.password = form.password;
        await adminUpdateUser(editing, payload);
        setMsg({ type:"ok", text:"Gespeichert." });
      }
      await load();
      setEditing(null);
    } catch(ex) { setMsg({ type:"err", text: ex.message }); }
    finally { setBusy(false); }
  }

  async function del(id) {
    if (!window.confirm("Benutzer wirklich löschen?")) return;
    try { await adminDeleteUser(id); await load(); } catch(ex) { setMsg({ type:"err", text: ex.message }); }
  }

  return (
    <div style={S.section}>
      {msg.text && <div style={msg.type==="ok" ? S.ok : S.err}>{msg.text}</div>}

      {editing ? (
        <form onSubmit={save}>
          <div style={{ fontSize:13, fontWeight:600, color:"#333", marginBottom:14 }}>
            {editing === "new" ? "Neuer Benutzer" : "Benutzer bearbeiten"}
          </div>
          {editing === "new" && <Field label="BENUTZERNAME" value={form.username||""} onChange={v=>setForm(f=>({...f,username:v}))} />}
          <Field label="ANZEIGENAME"  value={form.display_name||""} onChange={v=>setForm(f=>({...f,display_name:v}))} />
          <Field label="E-MAIL" type="email" value={form.email||""} onChange={v=>setForm(f=>({...f,email:v}))} />
          <Field label={editing==="new"?"PASSWORT":"NEUES PASSWORT (leer = unverändert)"} type="password" value={form.password||""} onChange={v=>setForm(f=>({...f,password:v}))} />
          <label style={S.label}>ROLLE</label>
          <select style={{ ...S.input }} value={form.role||"user"} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
            <option value="user">Benutzer</option>
            <option value="admin">Administrator</option>
          </select>
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ ...S.btn, ...S.btnDark, opacity:busy?0.6:1 }} disabled={busy}>Speichern</button>
            <button type="button" style={{ ...S.btn, ...S.btnGray }} onClick={() => setEditing(null)}>Abbrechen</button>
          </div>
        </form>
      ) : (
        <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ fontSize:12, color:"#bbb" }}>{users.length} Benutzer</div>
            <button style={{ ...S.btn, ...S.btnDark }} onClick={startNew}>+ Benutzer</button>
          </div>
          {users.map(u => (
            <div key={u.id} style={S.userRow}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:"#222" }}>
                  {u.display_name || u.username}
                  <span style={{ fontSize:10, color:"#bbb", fontWeight:400, marginLeft:6 }}>@{u.username}</span>
                </div>
                <div style={{ fontSize:11, color:"#bbb", marginTop:1 }}>
                  {u.email} · {u.progress_days} Übungstag{u.progress_days!==1?"e":""}
                </div>
              </div>
              <span style={{ ...S.badge, background: u.role==="admin"?"#e3f2fd":"#f0f0f0", color: u.role==="admin"?"#1565c0":"#888" }}>
                {u.role==="admin"?"Admin":"User"}
              </span>
              <button style={{ ...S.btn, ...S.btnGray, padding:"5px 10px" }} onClick={() => startEdit(u)}>✏️</button>
              {u.id !== me?.id && (
                <button style={{ ...S.btn, ...S.btnRed, padding:"5px 10px" }} onClick={() => del(u.id)}>🗑</button>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

export default function ProfileModal({ onClose }) {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("profile");
  const tabs = [{ id:"profile", label:"Mein Profil" }];
  if (user?.role === "admin") tabs.push({ id:"admin", label:"Benutzerverwaltung" });

  return (
    <div style={S.overlay} onClick={e => { if (e.target===e.currentTarget) onClose(); }}>
      <div style={S.sheet}>
        <div style={S.header}>
          <div style={S.title}>{user?.display_name || user?.username}</div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button style={{ ...S.btn, ...S.btnRed, padding:"5px 12px" }} onClick={logout}>Abmelden</button>
            <button style={S.close} onClick={onClose}>✕</button>
          </div>
        </div>

        {tabs.length > 1 && (
          <div style={S.tabs}>
            {tabs.map(t => (
              <button key={t.id} style={{ ...S.tab, color: tab===t.id?"#111":"#bbb", borderBottom: tab===t.id?"2px solid #1a1a1a":"2px solid transparent" }}
                onClick={() => setTab(t.id)}>{t.label}</button>
            ))}
          </div>
        )}

        {tab==="profile" && <ProfileTab />}
        {tab==="admin"   && <AdminTab />}
      </div>
    </div>
  );
}
